import type {
    CascaderProps,
    CheckboxProps,
    ColorPickerProps,
    DatePickerProps,
    InputNumberProps,
    InputProps,
    SelectProps,
    SwitchProps,
    UploadProps
} from 'antd';
import { ColorFactory } from 'antd/es/color-picker/color';
import type { RangePickerProps } from 'antd/es/date-picker';
import type { TextAreaProps } from 'antd/es/input';
import { PropsWithRef } from 'react';
import { AssigneeCascaderOptionType } from '../../hooks/ticketDetailPage/useCascaderAssigneeList';
import { ValueRequiredDateTimeFieldOperatorType } from '../AdminPage/ConditionInput/ConditionDateTimeInput';
import { CustomFieldOptionType, customFieldTypeMap } from '../CustomField/CustomField.type';
import { GrispiInputContainerProps } from './GrispiInputContainer';
import GrispiAssigneeInput from './components/GrispiAssigneeInput';
import GrispiAttachmentInput from './components/GrispiAttachmentInput';
import GrispiAutoCompleteInput, { AutoCompleteKeyMap } from './components/GrispiAutoCompleteInput';
import GrispiCheckboxInput from './components/GrispiCheckboxInput';
import GrispiColorPickerInput from './components/GrispiColorPickerInput';
import GrispiDateInput from './components/GrispiDateInput';
import GrispiDecimalInput from './components/GrispiDecimalInput';
import GrispiFormInput from './components/GrispiFormInput';
import GrispiIntegerInput from './components/GrispiIntegerInput';
import GrispiMultiAutocompleteInput from './components/GrispiMultiAutocompleteInput';
import GrispiMultiSelectInput from './components/GrispiMultiSelectInput';
import GrispiMultiUserInput from './components/GrispiMultiUserInput';
import GrispiPhoneInput from './components/GrispiPhoneInput';
import GrispiRichTextInput from './components/GrispiRichTextInput';
import GrispiSelectInput from './components/GrispiSelectInput';
import GrispiStatusInput from './components/GrispiStatusInput';
import GrispiSwitchInput from './components/GrispiSwitchInput';
import GrispiTagInput from './components/GrispiTagInput';
import GrispiTextAreaInput from './components/GrispiTextAreaInput';
import GrispiTextInput from './components/GrispiTextInput';
import GrispiUntrustedEndUserInput from './components/GrispiUntrustedEndUserInput';
import GrispiUserInput from './components/GrispiUserInput';

export const grispiInputTypeMap = {
    ...customFieldTypeMap,
    PHONE: 'PHONE',
    COLOR_PICKER: 'COLOR_PICKER',
    CHECKBOX: 'CHECKBOX',
    SWITCH: 'SWITCH',
    TEXTAREA: 'TEXTAREA',
    AUTOCOMPLETE: 'AUTOCOMPLETE',
    MULTI_AUTOCOMPLETE: 'MULTI_AUTOCOMPLETE'
} as const;
export const NOT_VALUE_REQUIRED_CONDITION_FIELD_TYPE = [grispiInputTypeMap.DATE, grispiInputTypeMap.DATE_TIME];

export type GrispiInputType = keyof typeof grispiInputTypeMap;
export type CustomFieldValueType<T extends GrispiInputType> = T extends
    | 'NUMBER_DECIMAL'
    | 'NUMBER_INTEGER'
    | 'FORM'
    | 'AUTOCOMPLETE'
    ? number | null
    : T extends 'SELECT'
    ? string | number | null
    : T extends 'COLOR_PICKER'
    ? ColorFactory
    : T extends
          | 'ASSIGNEE'
          | 'CHECKBOX'
          | 'SWITCH'
          | 'DATE'
          | 'DATE_TIME'
          | 'HTML'
          | 'USER'
          | 'MULTI_USER'
          | 'MULTI_SELECT'
          | 'MULTI_AUTOCOMPLETE'
          | 'UNTRUSTED_END_USER'
          | 'STATUS'
          | 'TAG'
          | 'TEXT'
          | 'TEXTAREA'
          | 'PHONE'
    ? string | null
    : unknown;

export interface GrispiInputProps<T extends GrispiInputType> {
    antdProps?: T extends 'HTML'
        ? never
        : T extends 'ATTACHMENTS'
        ? Partial<UploadProps>
        : T extends 'ASSIGNEE'
        ? CascaderProps<AssigneeCascaderOptionType>
        : T extends 'CHECKBOX'
        ? CheckboxProps
        : T extends 'COLOR_PICKER'
        ? ColorPickerProps
        : T extends 'SWITCH'
        ? SwitchProps
        : T extends 'DATE'
        ? DatePickerProps
        : T extends 'DATE_TIME'
        ? DatePickerProps | RangePickerProps
        : T extends 'NUMBER_DECIMAL' | 'NUMBER_INTEGER' | 'PHONE'
        ? InputNumberProps
        : T extends 'TEXTAREA'
        ? TextAreaProps
        : T extends 'TEXT' | 'UNTRUSTED_END_USER'
        ? InputProps
        : SelectProps & { ref?: any }; //TODO: Bünyamin => make compatible to type any
    buttonText?: T extends 'ATTACHMENTS' ? string : never;
    checkboxDefaultValue?: T extends 'CHECKBOX' ? null | string : never;
    conditionInput?: boolean;
    containerProps: GrispiInputContainerProps;
    disabled?: boolean;
    eventListenerKey?: T extends 'HTML' ? string : never;
    isAgentInput?: T extends 'MULTI_USER' | 'USER' ? boolean : never;
    isNewValueAllowed?: T extends 'TAG'
        ? true
        : T extends 'SELECT' | 'MULTI_SELECT' | 'TAG' | 'USER' | 'MULTI_USER' | 'UNTRUSTED_END_USER' | 'AUTOCOMPLETE'
        ? boolean
        : never;
    // easy reference for usage with any form
    onBlur?: T extends 'ATTACHMENTS' ? never : () => void;
    onChange?: (value: CustomFieldValueType<T>) => void;
    operator?: T extends 'DATE_TIME' ? ValueRequiredDateTimeFieldOperatorType : never;
    options?: T extends 'FORM' | 'STATUS' | 'SELECT' | 'MULTI_SELECT' | 'USER' | 'MULTI_USER' | 'AUTOCOMPLETE'
        ? Omit<CustomFieldOptionType, 'order'>[]
        : never;
    switchDefaultValue?: T extends 'SWITCH' ? null | string : never;
    toolbarId?: T extends 'HTML' ? number : never;
    type: T;
    value?: CustomFieldValueType<T>;
    withEmptyOption?: T extends 'ASSIGNEE' ? boolean : never;
    actionInput?: T extends 'ASSIGNEE' | 'STATUS' ? boolean : never;
    autocompleteKey?: T extends 'AUTOCOMPLETE' | 'MULTI_AUTOCOMPLETE' ? AutoCompleteKeyMap : never;
    fieldKey?: string;
    isEndUserForm?: T extends 'FORM' ? boolean : never;
}

const GrispiInput = <T extends GrispiInputType>({
    antdProps = {} as GrispiInputProps<T>['antdProps'],
    buttonText,
    checkboxDefaultValue,
    switchDefaultValue,
    conditionInput = false,
    actionInput,
    containerProps,
    disabled,
    eventListenerKey,
    isAgentInput,
    isNewValueAllowed,
    onBlur,
    onChange,
    operator,
    options,
    type,
    value,
    withEmptyOption,
    toolbarId,
    autocompleteKey,
    fieldKey,
    isEndUserForm,
    ...rest
}: PropsWithRef<GrispiInputProps<T>>) => {
    switch (type) {
        case 'ASSIGNEE':
            return (
                <GrispiAssigneeInput
                    antdProps={antdProps as GrispiInputProps<'ASSIGNEE'>['antdProps']}
                    conditionInput={conditionInput}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'ASSIGNEE'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'ASSIGNEE'>['onChange']}
                    withEmptyOption={withEmptyOption}
                    actionInput={actionInput}
                    {...rest}
                />
            );
        case 'ATTACHMENTS':
            return (
                <GrispiAttachmentInput
                    antdProps={antdProps as GrispiInputProps<'ATTACHMENTS'>['antdProps']}
                    buttonText={buttonText}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'ATTACHMENTS'>['value']}
                    onChange={onChange as GrispiInputProps<'ATTACHMENTS'>['onChange']}
                    {...rest}
                />
            );
        case 'CHECKBOX':
            return (
                <GrispiCheckboxInput
                    antdProps={antdProps as GrispiInputProps<'CHECKBOX'>['antdProps']}
                    checkboxDefaultValue={checkboxDefaultValue}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'CHECKBOX'>['value']}
                    onChange={onChange as GrispiInputProps<'CHECKBOX'>['onChange']}
                    {...rest}
                />
            );
        case 'DATE':
            return (
                <GrispiDateInput
                    antdProps={antdProps as GrispiInputProps<'DATE'>['antdProps']}
                    conditionInput={conditionInput}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'DATE'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'DATE'>['onChange']}
                    operator={operator}
                    type={type}
                    {...rest}
                />
            );
        case 'DATE_TIME':
            return (
                <GrispiDateInput
                    antdProps={
                        {
                            ...antdProps,
                            showTime: true,
                            format: 'DD/MM/YYYY HH:mm'
                        } as GrispiInputProps<'DATE_TIME'>['antdProps']
                    }
                    conditionInput={conditionInput}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'DATE_TIME'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'DATE_TIME'>['onChange']}
                    operator={operator}
                    type={type}
                    {...rest}
                />
            );
        case 'FORM':
            return (
                <GrispiFormInput
                    antdProps={antdProps as GrispiInputProps<'FORM'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'FORM'>['onChange']}
                    options={options as GrispiInputProps<'FORM'>['options']}
                    value={value as GrispiInputProps<'FORM'>['value']}
                    isEndUserForm={isEndUserForm as GrispiInputProps<'FORM'>['isEndUserForm']}
                    {...rest}
                />
            );
        case 'HTML':
            return conditionInput ? (
                <GrispiTextInput
                    antdProps={antdProps as GrispiInputProps<'TEXT'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'TEXT'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'TEXT'>['onChange']}
                    {...rest}
                />
            ) : (
                <GrispiRichTextInput
                    containerProps={containerProps}
                    disabled={disabled}
                    eventListenerKey={eventListenerKey}
                    value={value as GrispiInputProps<'HTML'>['value']}
                    onChange={onChange as GrispiInputProps<'TEXT'>['onChange']}
                    toolbarId={toolbarId}
                />
            );
        case 'NUMBER_DECIMAL':
            return (
                <GrispiDecimalInput
                    antdProps={antdProps as GrispiInputProps<'NUMBER_DECIMAL'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'NUMBER_DECIMAL'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'NUMBER_DECIMAL'>['onChange']}
                    {...rest}
                />
            );
        case 'NUMBER_INTEGER':
            return (
                <GrispiIntegerInput
                    antdProps={antdProps as GrispiInputProps<'NUMBER_INTEGER'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'NUMBER_INTEGER'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'NUMBER_INTEGER'>['onChange']}
                    {...rest}
                />
            );
        case 'STATUS':
            return (
                <GrispiStatusInput
                    antdProps={antdProps as GrispiInputProps<'STATUS'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'STATUS'>['onChange']}
                    options={options as GrispiInputProps<'STATUS'>['options']}
                    value={value as GrispiInputProps<'STATUS'>['value']}
                    actionInput={actionInput}
                    {...rest}
                />
            );
        case 'AUTOCOMPLETE':
            return (
                <GrispiAutoCompleteInput
                    autocompleteKey={autocompleteKey as GrispiInputProps<'AUTOCOMPLETE'>['autocompleteKey']}
                    antdProps={antdProps as GrispiInputProps<'AUTOCOMPLETE'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'AUTOCOMPLETE'>['onChange']}
                    options={options as GrispiInputProps<'AUTOCOMPLETE'>['options']}
                    value={value as GrispiInputProps<'AUTOCOMPLETE'>['value']}
                    {...rest}
                />
            );
        case 'MULTI_AUTOCOMPLETE':
            return (
                <GrispiMultiAutocompleteInput
                    autocompleteKey={autocompleteKey as GrispiInputProps<'MULTI_AUTOCOMPLETE'>['autocompleteKey']}
                    antdProps={antdProps as GrispiInputProps<'MULTI_AUTOCOMPLETE'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'MULTI_AUTOCOMPLETE'>['onChange']}
                    options={options as GrispiInputProps<'MULTI_AUTOCOMPLETE'>['options']}
                    value={value as GrispiInputProps<'MULTI_AUTOCOMPLETE'>['value']}
                    {...rest}
                />
            );
        case 'SELECT':
            return (
                <GrispiSelectInput
                    antdProps={antdProps as GrispiInputProps<'SELECT'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    isNewValueAllowed={isNewValueAllowed}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'SELECT'>['onChange']}
                    options={options as GrispiInputProps<'SELECT'>['options']}
                    value={value as GrispiInputProps<'SELECT'>['value']}
                    fieldKey={fieldKey as GrispiInputProps<'SELECT'>['fieldKey']}
                    {...rest}
                />
            );
        case 'SWITCH':
            return (
                <GrispiSwitchInput
                    antdProps={antdProps as GrispiInputProps<'SWITCH'>['antdProps']}
                    switchDefaultValue={switchDefaultValue}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'SWITCH'>['value']}
                    onChange={onChange as GrispiInputProps<'SWITCH'>['onChange']}
                    {...rest}
                />
            );
        case 'MULTI_SELECT':
            return (
                <GrispiMultiSelectInput
                    antdProps={antdProps as GrispiInputProps<'MULTI_SELECT'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    isNewValueAllowed={isNewValueAllowed}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'MULTI_SELECT'>['onChange']}
                    options={options as GrispiInputProps<'MULTI_SELECT'>['options']}
                    value={value as GrispiInputProps<'MULTI_SELECT'>['value']}
                    fieldKey={fieldKey as GrispiInputProps<'MULTI_SELECT'>['fieldKey']}
                    {...rest}
                />
            );
        case 'TAG':
            return (
                <GrispiTagInput
                    antdProps={antdProps as GrispiInputProps<'TAG'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    isNewValueAllowed={true}
                    value={value as GrispiInputProps<'TAG'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'TAG'>['onChange']}
                    {...rest}
                />
            );
        case 'TEXTAREA':
            return (
                <GrispiTextAreaInput
                    antdProps={antdProps as GrispiInputProps<'TEXTAREA'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'TEXTAREA'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'TEXTAREA'>['onChange']}
                    {...rest}
                />
            );
        case 'TEXT':
            return (
                <GrispiTextInput
                    antdProps={antdProps as GrispiInputProps<'TEXT'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'TEXT'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'TEXT'>['onChange']}
                    {...rest}
                />
            );
        case 'COLOR_PICKER':
            return (
                <GrispiColorPickerInput
                    antdProps={antdProps as GrispiInputProps<'COLOR_PICKER'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'COLOR_PICKER'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'COLOR_PICKER'>['onChange']}
                    {...rest}
                />
            );
        case 'PHONE':
            return (
                <GrispiPhoneInput
                    antdProps={antdProps as GrispiInputProps<'PHONE'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'PHONE'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'PHONE'>['onChange']}
                    {...rest}
                />
            );
        case 'USER':
            return (
                <GrispiUserInput
                    antdProps={antdProps as GrispiInputProps<'USER'>['antdProps']}
                    conditionInput={conditionInput}
                    containerProps={containerProps}
                    disabled={disabled}
                    isAgentInput={isAgentInput}
                    isNewValueAllowed={conditionInput ? false : isNewValueAllowed}
                    value={value as GrispiInputProps<'USER'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'USER'>['onChange']}
                    options={options}
                    {...rest}
                />
            );
        case 'MULTI_USER':
            return (
                <GrispiMultiUserInput
                    antdProps={antdProps as GrispiInputProps<'MULTI_USER'>['antdProps']}
                    conditionInput={conditionInput}
                    containerProps={containerProps}
                    disabled={disabled}
                    isAgentInput={isAgentInput}
                    isNewValueAllowed={conditionInput ? false : isNewValueAllowed}
                    value={value as GrispiInputProps<'MULTI_USER'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'MULTI_USER'>['onChange']}
                    options={options}
                    fieldKey={fieldKey as GrispiInputProps<'MULTI_USER'>['fieldKey']}
                    {...rest}
                />
            );
        case 'UNTRUSTED_END_USER':
            return conditionInput ? (
                <GrispiUserInput
                    antdProps={antdProps as GrispiInputProps<'USER'>['antdProps']}
                    containerProps={containerProps}
                    disabled={disabled}
                    isNewValueAllowed={false}
                    value={value as GrispiInputProps<'USER'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'USER'>['onChange']}
                    {...rest}
                />
            ) : (
                <GrispiUntrustedEndUserInput
                    containerProps={containerProps}
                    disabled={disabled}
                    value={value as GrispiInputProps<'UNTRUSTED_END_USER'>['value']}
                    onBlur={onBlur}
                    onChange={onChange as GrispiInputProps<'UNTRUSTED_END_USER'>['onChange']}
                    {...rest}
                />
            );
        default:
            return null;
    }
};

export default GrispiInput;
